import { useEffect, useMemo, useState } from "react";

const LIVE_LOG_KEY = "tacops_live_operation_logs_v1";
const LIVE_STATUS_KEY = "tacops_live_operation_status_v1";
const LIVE_CLOCK_KEY = "tacops_live_operation_clocks_v2";

const QUICK_EVENTS = [
  "Operation activated","Briefing started","Briefing complete","Team staged",
  "Command post established","EMS staged","Perimeter established","UAV/Drone Deployed",
  "Entry team staged","Movement initiated","Phase Line Red","Phase Line Yellow",
  "Phase Line Green","Breach Initiated","Diversion Device Deployed","Robot/Tech Deployed",
  "Gas Deployed","Less Lethal Deployed","Contact made","Subject detained",
  "Scene secure","Evidence handoff","Debrief started","Operation closed"
];

const UNIT_STATUSES = ["Not Assigned","Assigned","En Route","Staged","Deployed","Holding","Clear","Issue"];

function makeId(prefix = "live") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function loadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function defaultUnitStatus() {
  return {
    command: "Assigned",
    entry: "Not Assigned",
    perimeter: "Not Assigned",
    lessLethal: "Not Assigned",
    medical: "Not Assigned",
    drone: "Not Assigned",
    staging: "Not Assigned",
    transport: "Not Assigned"
  };
}

function defaultClock() {
  return { status: "stopped", startedAt: null, lastResumedAt: null, stoppedAt: null, accumulatedSeconds: 0 };
}

function elapsedSeconds(clock, nowMs) {
  const base = Number(clock?.accumulatedSeconds || 0);
  if (clock?.status === "running" && clock.lastResumedAt) {
    return base + Math.max(0, Math.floor((nowMs - new Date(clock.lastResumedAt).getTime()) / 1000));
  }
  return base;
}

function fmtElapsed(seconds) {
  const v = Math.max(0, Number(seconds || 0));
  return [Math.floor(v / 3600), Math.floor((v % 3600) / 60), v % 60]
    .map((n) => String(n).padStart(2, "0")).join(":");
}

function fmtTime(value) {
  try { return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
}

function fmtDateTime(value) {
  try { return new Date(value).toLocaleString(); }
  catch { return ""; }
}

function esc(value) {
  return String(value || "").replace(/[&<>"']/g, (m) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;"
  }[m]));
}

export default function LiveOperations({ operations = [], onUpdateOperation }) {
  const activeOps = operations.filter((op) => ["Planning", "Approved", "Active", "Debrief"].includes(op.status));
  const [selectedOperationId, setSelectedOperationId] = useState(activeOps[0]?.id || operations[0]?.id || "");
  const [logsByOperation, setLogsByOperation] = useState(() => loadJson(LIVE_LOG_KEY, {}));
  const [statusByOperation, setStatusByOperation] = useState(() => loadJson(LIVE_STATUS_KEY, {}));
  const [clocksByOperation, setClocksByOperation] = useState(() => loadJson(LIVE_CLOCK_KEY, {}));
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState("");
  const [now, setNow] = useState(Date.now());
  const [editingEntry, setEditingEntry] = useState(null);

  const selectedOperation = useMemo(
    () => operations.find((op) => op.id === selectedOperationId) || operations[0] || null,
    [operations, selectedOperationId]
  );

  const liveLog = selectedOperation ? logsByOperation[selectedOperation.id] || [] : [];
  const unitStatus = selectedOperation ? statusByOperation[selectedOperation.id] || defaultUnitStatus() : defaultUnitStatus();
  const clock = selectedOperation ? clocksByOperation[selectedOperation.id] || defaultClock() : defaultClock();
  const elapsed = elapsedSeconds(clock, now);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedOperationId && operations[0]?.id) setSelectedOperationId(operations[0].id);
  }, [operations, selectedOperationId]);

  function persistLogs(next) {
    setLogsByOperation(next);
    saveJson(LIVE_LOG_KEY, next);
  }

  function persistStatuses(next) {
    setStatusByOperation(next);
    saveJson(LIVE_STATUS_KEY, next);
  }

  function persistClocks(next) {
    setClocksByOperation(next);
    saveJson(LIVE_CLOCK_KEY, next);
  }

  function updateOperation(updatedOperation) {
    if (typeof onUpdateOperation === "function") onUpdateOperation(updatedOperation);
  }

  function addLog(title, detail = "", category = "Event") {
    if (!selectedOperation) return;

    const entry = { id: makeId(), title, detail, category, createdAt: new Date().toISOString() };
    const current = logsByOperation[selectedOperation.id] || [];
    const nextLog = [entry, ...current].slice(0, 250);
    persistLogs({ ...logsByOperation, [selectedOperation.id]: nextLog });

    updateOperation({
      ...selectedOperation,
      status: ["Planning", "Approved"].includes(selectedOperation.status) ? "Active" : selectedOperation.status,
      updatedAt: entry.createdAt,
      timeline: [
        { id: `tl_${entry.id}`, time: entry.createdAt, title, detail: detail || category },
        ...(Array.isArray(selectedOperation.timeline) ? selectedOperation.timeline : [])
      ]
    });
  }

  function syncTimeline(nextLog) {
    if (!selectedOperation) return;
    const existing = (selectedOperation.timeline || []).filter((item) => !String(item.id || "").startsWith("tl_live_"));
    const liveTimeline = nextLog.map((entry) => ({
      id: `tl_${entry.id}`,
      time: entry.createdAt,
      title: entry.title,
      detail: entry.detail || entry.category || "Live Operations"
    }));
    updateOperation({ ...selectedOperation, updatedAt: new Date().toISOString(), timeline: [...liveTimeline, ...existing] });
  }

  function editLogEntry(entry) {
    setEditingEntry({ id: entry.id, title: entry.title || "", detail: entry.detail || "", category: entry.category || "Event" });
  }

  function saveEditedEntry(e) {
    e.preventDefault();
    if (!selectedOperation || !editingEntry) return;
    if (!editingEntry.title.trim()) return alert("Event title is required.");

    const current = logsByOperation[selectedOperation.id] || [];
    const nextLog = current.map((entry) =>
      entry.id === editingEntry.id
        ? { ...entry, title: editingEntry.title.trim(), detail: editingEntry.detail.trim(), category: editingEntry.category.trim() || "Event", editedAt: new Date().toISOString() }
        : entry
    );

    persistLogs({ ...logsByOperation, [selectedOperation.id]: nextLog });
    syncTimeline(nextLog);
    setEditingEntry(null);
  }

  function deleteLogEntry(id) {
    if (!selectedOperation) return;
    if (!confirm("Delete this live event log entry?")) return;

    const current = logsByOperation[selectedOperation.id] || [];
    const nextLog = current.filter((entry) => entry.id !== id);
    persistLogs({ ...logsByOperation, [selectedOperation.id]: nextLog });
    syncTimeline(nextLog);
  }

  function clearLiveLog() {
    if (!selectedOperation) return;
    if (!confirm("Clear the entire live event log for this operation?")) return;

    persistLogs({ ...logsByOperation, [selectedOperation.id]: [] });
    syncTimeline([]);
  }

  function exportLiveLog() {
    if (!selectedOperation) return;

    const entries = [...liveLog].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    const unitRows = Object.entries(unitStatus);
    const clockStatus = String(clock.status || "stopped").toUpperCase();
    const elapsedText = fmtElapsed(elapsed);

    const win = window.open("", "_blank");
    if (!win) return alert("Popup blocked. Allow popups and try again.");

    win.document.write(`
      <html>
        <head>
          <title>TacOps Live Operations Chronology</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 36px; color: #111; }
            h1 { margin-bottom: 4px; letter-spacing: .03em; }
            h2 { border-bottom: 1px solid #999; padding-bottom: 4px; margin-top: 24px; }
            .sub { color: #555; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
            .box { border: 1px solid #ccc; padding: 10px; margin: 8px 0; page-break-inside: avoid; }
            .label { font-size: 11px; color: #555; text-transform: uppercase; font-weight: bold; }
            .value { margin-top: 4px; white-space: pre-wrap; line-height: 1.35; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #ccc; padding: 8px; vertical-align: top; text-align: left; }
            th { background: #eee; font-size: 12px; text-transform: uppercase; }
            @media print { button { display:none; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()">Print / Save as PDF</button>
          <h1>TacOps Live Operations Chronology</h1>
          <div class="sub">TacOps by Aegis Group</div>

          <h2>Operation Summary</h2>
          <div class="grid">
            <div class="box"><div class="label">Operation</div><div class="value">${esc(selectedOperation.title)}</div></div>
            <div class="box"><div class="label">Operation Type</div><div class="value">${esc(selectedOperation.operationType || selectedOperation.missionType)}</div></div>
            <div class="box"><div class="label">Location</div><div class="value">${esc(selectedOperation.location)}</div></div>
            <div class="box"><div class="label">Commander</div><div class="value">${esc(selectedOperation.commander)}</div></div>
            <div class="box"><div class="label">Clock Status</div><div class="value">${esc(clockStatus)}</div></div>
            <div class="box"><div class="label">Elapsed Time</div><div class="value">${esc(elapsedText)}</div></div>
          </div>

          <div class="box"><div class="label">Objective</div><div class="value">${esc(selectedOperation.objective)}</div></div>

          <h2>Unit Accountability</h2>
          <table>
            <thead><tr><th>Element</th><th>Status</th></tr></thead>
            <tbody>
              ${unitRows.map(([unit, status]) => `<tr><td>${esc(unitLabel(unit))}</td><td>${esc(status)}</td></tr>`).join("")}
            </tbody>
          </table>

          <h2>Live Event Log</h2>
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Category</th>
                <th>Event</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              ${entries.map((entry) => `
                <tr>
                  <td>${esc(fmtDateTime(entry.createdAt))}</td>
                  <td>${esc(entry.category)}</td>
                  <td>${esc(entry.title)}</td>
                  <td>${esc(entry.detail)}${entry.editedAt ? `<br><small>Edited: ${esc(fmtDateTime(entry.editedAt))}</small>` : ""}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);

    win.document.close();
  }

  function setClock(nextClock) {
    if (!selectedOperation) return;
    persistClocks({ ...clocksByOperation, [selectedOperation.id]: nextClock });
  }

  function startClock() {
    if (!selectedOperation) return;
    const timestamp = new Date().toISOString();
    setClock({ status: "running", startedAt: clock.startedAt || timestamp, lastResumedAt: timestamp, stoppedAt: null, accumulatedSeconds: Number(clock.accumulatedSeconds || 0) });
    addLog("Live operation activated", "Operation clock started.", "Activation");
    updateOperation({ ...selectedOperation, status: "Active", updatedAt: timestamp });
  }

  function pauseClock() {
    if (clock.status !== "running") return;
    const total = elapsedSeconds(clock, Date.now());
    setClock({ ...clock, status: "paused", lastResumedAt: null, stoppedAt: null, accumulatedSeconds: total });
    addLog("Operation clock paused", `Elapsed time: ${fmtElapsed(total)}`, "Clock");
  }

  function resumeClock() {
    if (!selectedOperation || clock.status === "running") return;
    const timestamp = new Date().toISOString();
    setClock({ ...clock, status: "running", startedAt: clock.startedAt || timestamp, lastResumedAt: timestamp, stoppedAt: null, accumulatedSeconds: Number(clock.accumulatedSeconds || 0) });
    addLog("Operation clock resumed", `Elapsed time: ${fmtElapsed(clock.accumulatedSeconds || 0)}`, "Clock");
  }

  function stopClock() {
    if (!selectedOperation) return;
    const total = elapsedSeconds(clock, Date.now());
    setClock({ ...clock, status: "stopped", lastResumedAt: null, stoppedAt: new Date().toISOString(), accumulatedSeconds: total });
    addLog("Operation clock stopped", `Final elapsed time: ${fmtElapsed(total)}`, "Clock");
  }

  function resetClock() {
    if (!selectedOperation) return;
    if (!confirm("Reset the operation clock for this operation?")) return;
    setClock(defaultClock());
    addLog("Operation clock reset", "Elapsed time reset to 00:00:00.", "Clock");
  }

  function endOperation() {
    if (!selectedOperation) return;
    const total = elapsedSeconds(clock, Date.now());
    setClock({ ...clock, status: "ended", lastResumedAt: null, stoppedAt: new Date().toISOString(), accumulatedSeconds: total });
    addLog("Operation ended", `Final elapsed time: ${fmtElapsed(total)}. Operation moved to debrief.`, "Closure");
    updateOperation({ ...selectedOperation, status: "Debrief", updatedAt: new Date().toISOString() });
  }

  function submitNote(e) {
    e.preventDefault();
    if (!note.trim()) return;
    addLog("Command note", note.trim(), "Command Note");
    setNote("");
  }

  function submitDecision(e) {
    e.preventDefault();
    if (!decision.trim()) return;
    addLog("Command decision", decision.trim(), "Decision");
    setDecision("");
  }

  function updateUnit(unit, value) {
    if (!selectedOperation) return;
    const current = statusByOperation[selectedOperation.id] || defaultUnitStatus();
    persistStatuses({ ...statusByOperation, [selectedOperation.id]: { ...current, [unit]: value } });
    addLog(`${unitLabel(unit)} status changed`, value, "Status");
  }

  if (!operations.length) {
    return <section className="panel"><h2>Live Operations</h2><p className="muted">Create an operation first, then activate Live Operations mode.</p></section>;
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Live Operations</h2>
          <p className="muted">Runtime command board, accountability, clock control, exportable chronology, and editable live event log.</p>
        </div>
        <div className={`live-clock ${clock.status}`}>
          <span>OP CLOCK • {String(clock.status || "stopped").toUpperCase()}</span>
          <b>{fmtElapsed(elapsed)}</b>
        </div>
      </div>

      <div className="live-layout">
        <div className="panel">
          <div className="section-head"><div><h2>Active Operation</h2><p className="muted">Select an operation and activate live tracking.</p></div></div>

          <label>
            <span>Operation</span>
            <select value={selectedOperationId} onChange={(e) => setSelectedOperationId(e.target.value)}>
              {operations.map((op) => <option key={op.id} value={op.id}>{op.title}</option>)}
            </select>
          </label>

          {selectedOperation ? (
            <div className="live-op-card">
              <div className="mission-title">{selectedOperation.title}</div>
              <div className="mission-meta">{selectedOperation.operationType || selectedOperation.missionType || "Operation"} • {selectedOperation.location || "No location"} • {selectedOperation.status}</div>
              <p>{selectedOperation.objective || "No objective entered."}</p>

              <div className="chips">
                <span className={`badge ${selectedOperation.priority === "Critical" ? "danger" : selectedOperation.priority === "High" ? "warn" : "neutral"}`}>{selectedOperation.priority}</span>
                <span className="badge neutral">Commander: {selectedOperation.commander || "TBD"}</span>
              </div>

              <div className="live-actions">
                <button className="primary" onClick={startClock}>Start</button>
                <button onClick={pauseClock}>Pause</button>
                <button onClick={resumeClock}>Resume</button>
                <button onClick={stopClock}>Stop</button>
                <button onClick={resetClock}>Reset</button>
                <button onClick={exportLiveLog}>Export Log</button>
                <button className="danger-text" onClick={endOperation}>End Operation</button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="panel">
          <div className="section-head"><div><h2>Quick Events</h2><p className="muted">One-click timestamped log entries.</p></div></div>
          <div className="quick-event-grid">
            {QUICK_EVENTS.map((event) => <button key={event} onClick={() => addLog(event, "", "Quick Event")}>{event}</button>)}
          </div>
        </div>
      </div>

      <div className="two-col">
        <div className="panel">
          <div className="section-head"><div><h2>Unit Accountability</h2><p className="muted">Track live team element status.</p></div></div>
          <div className="unit-status-list">
            {Object.entries(unitStatus).map(([unit, status]) => (
              <div className="unit-status-row" key={unit}>
                <div><b>{unitLabel(unit)}</b><small>{status}</small></div>
                <select value={status} onChange={(e) => updateUnit(unit, e.target.value)}>
                  {UNIT_STATUSES.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="section-head"><div><h2>Command Notes</h2><p className="muted">Capture decisions and notes for AAR/timeline use.</p></div></div>
          <form onSubmit={submitNote}>
            <label><span>Command Note</span><textarea rows="4" value={note} onChange={(e) => setNote(e.target.value)} /></label>
            <button className="primary" type="submit">Add Note</button>
          </form>
          <hr className="soft-rule" />
          <form onSubmit={submitDecision}>
            <label><span>Command Decision</span><textarea rows="4" value={decision} onChange={(e) => setDecision(e.target.value)} /></label>
            <button className="primary" type="submit">Log Decision</button>
          </form>
        </div>
      </div>

      <div className="panel">
        <div className="section-head">
          <div><h2>Live Event Log</h2><p className="muted">Timestamped operational chronology for timeline and AAR reconstruction.</p></div>
          <div className="live-log-toolbar">
            <button onClick={exportLiveLog}>Export Log</button>
            <button className="danger-text" onClick={clearLiveLog}>Clear Log</button>
          </div>
        </div>

        {editingEntry ? (
          <form className="live-edit-form" onSubmit={saveEditedEntry}>
            <div className="form-grid">
              <label><span>Event Title</span><input value={editingEntry.title} onChange={(e) => setEditingEntry({ ...editingEntry, title: e.target.value })} /></label>
              <label><span>Category</span><input value={editingEntry.category} onChange={(e) => setEditingEntry({ ...editingEntry, category: e.target.value })} /></label>
            </div>
            <label className="full"><span>Details</span><textarea rows="3" value={editingEntry.detail} onChange={(e) => setEditingEntry({ ...editingEntry, detail: e.target.value })} /></label>
            <div className="modal-actions">
              <button type="button" onClick={() => setEditingEntry(null)}>Cancel</button>
              <button className="primary" type="submit">Save Entry</button>
            </div>
          </form>
        ) : null}

        {liveLog.length === 0 ? (
          <div className="empty">No live events logged yet.</div>
        ) : (
          <div className="live-log">
            {liveLog.map((entry) => (
              <div className="live-log-entry" key={entry.id}>
                <div className="live-log-time">{fmtTime(entry.createdAt)}</div>
                <div>
                  <b>{entry.title}</b>
                  <small>{entry.category} • {fmtDateTime(entry.createdAt)}{entry.editedAt ? ` • Edited ${fmtDateTime(entry.editedAt)}` : ""}</small>
                  {entry.detail ? <p>{entry.detail}</p> : null}
                  <div className="live-log-actions">
                    <button onClick={() => editLogEntry(entry)}>Edit</button>
                    <button className="danger-text" onClick={() => deleteLogEntry(entry.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function unitLabel(unit) {
  const labels = {
    command: "Command",
    entry: "Entry Team",
    perimeter: "Perimeter",
    lessLethal: "Less-Lethal",
    medical: "Medical / EMS",
    drone: "UAV / Drone",
    staging: "Staging",
    transport: "Transport"
  };
  return labels[unit] || unit;
}
